import { Product } from "@/api/product-api";
import { useBhapticsContents } from "@/hooks/useBhapticsContents";
import { cn } from "@/libs/utils";
import { useGetBundleProductComponents } from "@/services/product/useProductService";
import { GameUtil } from "@/utils/GameUtil";
import { ProductUtil } from "@/utils/ProductUtil";
import { useTrans } from "@/utils/useTrans";
import TactSuitCard from "@/v2-components/TactSuitCard";
import { RailCardPrice } from "./rail-card-price";

export function BundleUpgradeSelect({
  bundles,
  baseProduct,
  selectedProduct,
  hasSelected,
  onSelect,
}: {
  bundles: Product[];
  baseProduct: Product;
  selectedProduct: Product | null;
  hasSelected: boolean;
  onSelect: (product: Product) => void;
}) {
  const { t } = useTrans(["shop", "gnb"]);
  const isNoUpgradeSelected =
    hasSelected && selectedProduct?.slug === baseProduct.slug;

  return (
    <div>
      {/* 제목과 "Please select an option" 보조줄은 **호출처 AccordionTrigger** 가 갖는다.
          피그마(28715:139567)에서 보조줄이 헤더 블록 안이라 **접힌 상태에서도 보인다** —
          여기 두면 아코디언을 열어야 보여서 미선택 경고 역할을 못 한다.
          같은 문구를 두 곳에서 그리지 않기 위해서도 한쪽으로 모은다.
          카드 간 간격 12px, 헤더→리스트 20px 는 AccordionContent 의 pt-5 가 담당. */}
      <div className="flex flex-col gap-y-3">
        {bundles.map((bundle) => (
          <BundleCard
            key={bundle.slug}
            product={bundle}
            selected={hasSelected && selectedProduct?.slug === bundle.slug}
            onClick={() => onSelect(bundle)}
          />
        ))}
        <button
          type="button"
          onClick={() => onSelect(baseProduct)}
          className={cn(
            "rounded-lg border border-coolgray-20 bg-white p-5 text-left text-lg font-600 tracking-[0.5px] text-coolgray-90 transition-colors",
            // 위 카드와 같은 이유로 border-2 대신 border+inset ring (점프 방지)
            isNoUpgradeSelected
              ? "border-blue-60 ring-1 ring-inset ring-blue-60"
              : "hover:border-coolgray-90",
          )}
        >
          {t("No upgrade")}
        </button>
      </div>
    </div>
  );
}

const BundleCard = ({
  product,
  selected,
  onClick,
}: {
  product: Product;
  selected: boolean;
  onClick: () => void;
}) => {
  const components = useGetBundleProductComponents(product);
  const gameResellIds = ProductUtil.getProductGameResellIds(product);
  const { contents } = useBhapticsContents();
  const games = GameUtil.getGames({ contents, gameResellIds });

  const composition = [
    ...components.map((p) => p.name),
    ...games.map((g) => g.name),
  ].join(" + ");

  return (
    <TactSuitCard product={product}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "group w-full overflow-hidden rounded-lg border border-coolgray-20 bg-white px-4 pb-3 pt-4 text-left transition-colors",
          // 선택 = 2px 파란 테두리(피그마 28715:139630). 단 `border-2` 로 구현하면
          // 카드가 콘텐츠 높이 기반이라 **선택 순간 높이 +2px · 텍스트 +1px 밀린다**
          // (실측). border 1px + inset ring 1px = 시각적으로 같은 2px 이면서
          // 레이아웃 영향 0. 사이즈 칩·썸네일은 고정 크기라 border-2 로 괜찮다.
          selected
            ? "border-blue-60 ring-1 ring-inset ring-blue-60"
            : "hover:border-coolgray-90",
        )}
      >
        <div className="flex items-center gap-x-4">
          <div className="relative aspect-square w-full max-w-[114px] shrink-0">
            <TactSuitCard.Thumbnail fill />
          </div>
          <div className="flex flex-col gap-y-3">
            <TactSuitCard.Name className="text-lg font-600 tracking-[0.5px]" />
            {composition && (
              <p className="text-base tracking-[0.25px] text-coolgray-90">
                {composition}
              </p>
            )}
            <RailCardPrice product={product} />
          </div>
        </div>
      </button>
    </TactSuitCard>
  );
};
